import { defaultToastProps } from 'appConstants';
import { Languages, SelectedLanguage, SetStateType, TranslateFiled } from 'commonTypes';
import { Spinner } from 'components/Common/Spinner';
import { fetchLambdaTranslate } from 'helpers/translate';
import { checkFieldNotEmpty } from 'helpers/validation';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { returnActiveSubscription } from 'store/subscription/actions';
import { showSubscriptionModal } from 'store/subscriptionNotification/actions';

interface AddCategoryButtonProps {
  edit: boolean;
  activeStep: number;
  categoryName: string;
  addCategoryTranslate: object | Record<string, never> | Record<Languages, string>;
  selected: SelectedLanguage | undefined;
  setAddCategoryTranslate: SetStateType<Record<string, string>>;
  setLoadingTranslateLambda: SetStateType<boolean>;
  setActiveStep: SetStateType<number>;
  description: string;
  descriptionTranslate: object | Record<string, never> | Record<Languages, string>;
  setDescriptionTranslate: SetStateType<Record<string, string>>;
  handleCreateCategory: () => void;
  loadingTranslateLambda: boolean;
  selectedDescription: SelectedLanguage | undefined;
  descriptionOld: Record<string, never> | TranslateFiled | undefined;
  setOpen: SetStateType<boolean>;
  resetFields: () => void;
  name: TranslateFiled | undefined;
  withoutTranslationsName: boolean;
  withoutTranslationsDescription: boolean;
}

export const AddCategoryButton = ({
  edit,
  activeStep,
  categoryName,
  addCategoryTranslate,
  selected,
  setAddCategoryTranslate,
  setLoadingTranslateLambda,
  setActiveStep,
  description,
  descriptionTranslate,
  setDescriptionTranslate,
  handleCreateCategory,
  loadingTranslateLambda,
  selectedDescription,
  descriptionOld,
  setOpen,
  resetFields,
  name,
  withoutTranslationsName,
  withoutTranslationsDescription,
}: AddCategoryButtonProps) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.category.subLoading);
  const { isSubscriptionInvalid } = returnActiveSubscription();

  const handleSaveClick = () => {
    if (isSubscriptionInvalid) {
      dispatch(showSubscriptionModal());
      resetFields();
      return;
    }
    if (activeStep === 1) {
      if (categoryName.trim() && selected?.value) {
        if (
          addCategoryTranslate[selected?.value as keyof typeof addCategoryTranslate] !==
            categoryName &&
          !withoutTranslationsName
        ) {
          setAddCategoryTranslate({});
          dispatch(
            fetchLambdaTranslate({
              setLoading: setLoadingTranslateLambda,
              setData: setAddCategoryTranslate,
              text: categoryName.trim(),
              source_lang: selected.value,
              setActiveStep,
            }),
          );
        } else if (withoutTranslationsName) {
          setActiveStep(3);
        }
      } else {
        toast('Category name required', {
          ...defaultToastProps,
          type: 'error',
        });
      }
    } else if (activeStep === 2) {
      if (checkFieldNotEmpty(addCategoryTranslate)) {
        setActiveStep(3);
      } else {
        toast('Translations category name required', {
          ...defaultToastProps,
          type: 'error',
        });
      }
    } else if (activeStep === 3) {
      if (description.trim() && selectedDescription?.value) {
        if (
          descriptionTranslate[
            selectedDescription?.value as keyof typeof descriptionTranslate
          ] !== description &&
          !withoutTranslationsDescription
        ) {
          setDescriptionTranslate({});
          dispatch(
            fetchLambdaTranslate({
              setLoading: setLoadingTranslateLambda,
              setData: setDescriptionTranslate,
              text: description.trim(),
              source_lang: selectedDescription.value,
              setActiveStep,
            }),
          );
        } else {
          if (
            Object.keys(descriptionTranslate).length > 0 &&
            !withoutTranslationsDescription
          ) {
            setActiveStep(4);
          } else if (
            Object.keys(descriptionTranslate).length === 0 ||
            withoutTranslationsDescription
          ) {
            handleCreateCategory();
          }
        }
      } else {
        handleCreateCategory();
      }
    } else if (activeStep === 4) {
      handleCreateCategory();
    }
  };

  if (edit) {
    return (
      <div>
        {activeStep === 1 ? (
          // @ts-ignore
          name.translations[name.source_lang] === categoryName ? (
            <button
              disabled={loadingTranslateLambda}
              onClick={() => {
                if (withoutTranslationsName) {
                  setActiveStep(3);
                } else {
                  setActiveStep(2);
                }
              }}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm md:hover:bg-indigo-700 md:focus:outline-none md:focus:ring-2 md:focus:ring-indigo-500 md:focus:ring-offset-2"
            >
              <FormattedMessage id="next" />
            </button>
          ) : (
            <button
              disabled={loadingTranslateLambda}
              onClick={() => {
                if (isSubscriptionInvalid) {
                  dispatch(showSubscriptionModal());
                  resetFields();
                  return;
                }
                if (categoryName.trim()) {
                  if (withoutTranslationsName) {
                    setActiveStep(3);
                  } else {
                    setAddCategoryTranslate({});
                    dispatch(
                      fetchLambdaTranslate({
                        setLoading: setLoadingTranslateLambda,
                        setData: setAddCategoryTranslate,
                        text: categoryName.trim(),
                        // @ts-ignore
                        source_lang: selected?.value,
                        setActiveStep,
                      }),
                    );
                  }
                } else {
                  toast('Category name required', {
                    ...defaultToastProps,
                    type: 'error',
                  });
                }
              }}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm md:hover:bg-indigo-700 md:focus:outline-none md:focus:ring-2 md:focus:ring-indigo-500 md:focus:ring-offset-2"
            >
              {loading || loadingTranslateLambda ? (
                <Spinner color="bg-white" />
              ) : (
                <FormattedMessage id="savengonext" />
              )}
            </button>
          )
        ) : activeStep === 2 ? (
          <button
            disabled={loadingTranslateLambda}
            onClick={() => {
              if (isSubscriptionInvalid) {
                dispatch(showSubscriptionModal());
                resetFields();
                return;
              }
              if (checkFieldNotEmpty(addCategoryTranslate)) {
                setActiveStep(3);
              } else {
                toast('Translations category name required', {
                  ...defaultToastProps,
                  type: 'error',
                });
              }
            }}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm md:hover:bg-indigo-700 md:focus:outline-none md:focus:ring-2 md:focus:ring-indigo-500 md:focus:ring-offset-2"
          >
            <FormattedMessage id="next" />
          </button>
        ) : activeStep === 3 ? (
          descriptionOld?.source_lang &&
          descriptionOld?.translations &&
          (descriptionOld?.translations[descriptionOld?.source_lang] || '') ===
            description ? (
            <button
              disabled={loadingTranslateLambda}
              onClick={() => {
                if (isSubscriptionInvalid) {
                  dispatch(showSubscriptionModal());
                  resetFields();
                  return;
                }
                if (description.trim() && !withoutTranslationsDescription) {
                  setActiveStep(4);
                } else {
                  handleCreateCategory();
                }
              }}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm md:hover:bg-indigo-700 md:focus:outline-none md:focus:ring-2 md:focus:ring-indigo-500 md:focus:ring-offset-2"
            >
              {description.trim() && !withoutTranslationsDescription ? (
                <FormattedMessage id="next" />
              ) : (
                <FormattedMessage id="save" />
              )}
            </button>
          ) : (
            <button
              disabled={loadingTranslateLambda}
              onClick={() => {
                if (isSubscriptionInvalid) {
                  dispatch(showSubscriptionModal());
                  resetFields();
                  return;
                }
                if (description) {
                  if (withoutTranslationsDescription) {
                    handleCreateCategory();
                  } else {
                    setDescriptionTranslate({});
                    dispatch(
                      fetchLambdaTranslate({
                        setLoading: setLoadingTranslateLambda,
                        setData: setDescriptionTranslate,
                        text: description.trim(),
                        source_lang: selectedDescription?.value || 'en',
                        setActiveStep,
                      }),
                    );
                  }
                } else {
                  handleCreateCategory();
                }
              }}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm md:hover:bg-indigo-700 md:focus:outline-none md:focus:ring-2 md:focus:ring-indigo-500 md:focus:ring-offset-2"
            >
              {loading || loadingTranslateLambda ? (
                <Spinner color="bg-white" />
              ) : (
                <FormattedMessage id="savengonext" />
              )}
            </button>
          )
        ) : JSON.stringify(name?.translations) !== JSON.stringify(addCategoryTranslate) ||
          JSON.stringify(descriptionOld?.translations || {}) !==
            JSON.stringify(descriptionTranslate) ? (
          <button
            disabled={loadingTranslateLambda}
            onClick={() => handleCreateCategory()}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm md:hover:bg-indigo-700 md:focus:outline-none md:focus:ring-2 md:focus:ring-indigo-500 md:focus:ring-offset-2"
          >
            {loading ? (
              <Spinner color="bg-white" />
            ) : edit ? (
              <FormattedMessage id="edit" />
            ) : (
              <FormattedMessage id="next" />
            )}
          </button>
        ) : (
          <button
            disabled={loadingTranslateLambda}
            onClick={() => {
              setOpen(false);
              setTimeout(() => {
                resetFields();
              }, 250);
            }}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm md:hover:bg-indigo-700 md:focus:outline-none md:focus:ring-2 md:focus:ring-indigo-500 md:focus:ring-offset-2"
          >
            <FormattedMessage id="save" />
          </button>
        )}
      </div>
    );
  } else {
    return (
      <button
        data-testId="cypress-add-category-next"
        disabled={loadingTranslateLambda}
        onClick={() => handleSaveClick()}
        className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm md:hover:bg-indigo-700 md:focus:outline-none md:focus:ring-2 md:focus:ring-indigo-500 md:focus:ring-offset-2"
      >
        {loading || loadingTranslateLambda ? (
          <Spinner color="bg-white" />
        ) : (activeStep === 3 && description.trim() === '') ||
          activeStep === 4 ||
          (activeStep === 3 && withoutTranslationsDescription) ? (
          <FormattedMessage id="save" />
        ) : (
          <FormattedMessage id="next" />
        )}
      </button>
    );
  }
};
